import type { ExportModalEntity } from "./types";

/**
 * All known service name variants for "Entity Plus" across different API
 * responses and environments. The backend uses different naming conventions
 * depending on the endpoint:
 *   - "Entities Plus" - common in service listing responses
 *   - "ENTITIES_PLUS" - enum-style constant used in backend filters
 *   - "Entities +"    - display name used in entity detail `active_services`
 *
 * We match against all variants to avoid silent mismatches when the API
 * returns a format we didn't anticipate.
 */
const ENTITY_PLUS_SERVICE_NAMES = [
  "Entities Plus",
  "ENTITIES_PLUS",
  "Entities +",
];

/**
 * Checks whether an entity has the "Entity Plus" service enabled by
 * inspecting its `services` array. Used to conditionally show Entity
 * Plus-gated datasets (PoA, Officers, Entity Plus Details) in the
 * export modal (GXM-1879).
 *
 * The check is defensive: trims whitespace and coerces to string before
 * matching, so minor API inconsistencies don't cause false negatives.
 *
 * @param entity - Entity object from the export modal (may come from the
 *   list endpoint's `services` or the detail endpoint's `active_services`)
 * @returns `true` if the entity has any service matching an Entity Plus name variant
 */
export function hasEntityPlus(entity: ExportModalEntity): boolean {
  const services = entity.services;
  if (!Array.isArray(services) || services.length === 0) {
    return false;
  }
  return services.some(
    (s) =>
      s?.name != null &&
      ENTITY_PLUS_SERVICE_NAMES.includes(String(s.name).trim()),
  );
}
